import { useState, useMemo } from 'react';
import { Search, Star, MoreVertical, Trash2, Pencil, SquarePen } from 'lucide-react';
import type { BreakdownTemplate } from '@jobos/shared';
import { Input } from '@/components/ui/input';
import { DropdownButtonMenu } from '@/components/ui/dropdown-button-menu';
import { cn } from '@/lib/utils';
import { isTemplateEditable } from '../templateEditability';

interface Props {
  templates: BreakdownTemplate[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
  onRename: (id: string) => void;
  onSetDefault: (id: string) => void;
  onEditContents: (id: string) => void;
}

export function TemplateSidebar({
  templates,
  selectedId,
  onSelect,
  onDelete,
  onRename,
  onSetDefault,
  onEditContents,
}: Props) {
  const [query, setQuery] = useState('');

  const grouped = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = q
      ? templates.filter(
          (t) =>
            t.name.toLowerCase().includes(q) || t.projectType.toLowerCase().includes(q)
        )
      : templates;
    const map = new Map<string, BreakdownTemplate[]>();
    for (const t of filtered) {
      if (!map.has(t.projectType)) map.set(t.projectType, []);
      map.get(t.projectType)!.push(t);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [templates, query]);

  return (
    <div className="w-80 flex-shrink-0 bg-white shadow rounded-lg overflow-hidden flex flex-col">
      <div className="p-4 border-b border-gray-200">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search templates..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
        {grouped.length === 0 ? (
          <div className="p-6 text-center text-gray-500 text-sm">
            No templates yet. Create one from a job's breakdown page.
          </div>
        ) : (
          grouped.map(([projectType, list]) => (
            <div key={projectType}>
              <div className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500 bg-gray-50 border-b border-gray-100">
                {projectType}
              </div>
              {list.map((t) => (
                <div
                  key={t.id}
                  className={cn(
                    'group flex items-center justify-between hover:bg-gray-50 transition-colors border-b border-gray-100',
                    selectedId === t.id && 'bg-gray-100'
                  )}
                >
                  <button
                    onClick={() => onSelect(t.id)}
                    className="flex-1 px-4 py-3 text-left"
                  >
                    <div className="flex items-center gap-2">
                      {t.isDefault && (
                        <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-500" />
                      )}
                      <div className="text-sm font-medium text-gray-900">{t.name}</div>
                    </div>
                  </button>
                  <DropdownButtonMenu
                    icon={<MoreVertical className="h-4 w-4" />}
                    ariaLabel="Template actions"
                    className="mr-2 opacity-0 group-hover:opacity-100"
                    items={[
                      {
                        label: isTemplateEditable(t.projectType)
                          ? 'Edit contents'
                          : 'Edit contents (coming soon)',
                        icon: <SquarePen className="h-4 w-4" />,
                        onClick: () => onEditContents(t.id),
                        disabled: !isTemplateEditable(t.projectType),
                      },
                      {
                        label: 'Rename',
                        icon: <Pencil className="h-4 w-4" />,
                        onClick: () => onRename(t.id),
                      },
                      ...(t.isDefault
                        ? []
                        : [
                            {
                              label: 'Set as default',
                              icon: <Star className="h-4 w-4" />,
                              onClick: () => onSetDefault(t.id),
                            },
                          ]),
                      {
                        label: 'Delete',
                        icon: <Trash2 className="h-4 w-4" />,
                        onClick: () => onDelete(t.id),
                        variant: 'destructive' as const,
                      },
                    ]}
                  />
                </div>
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
